import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import useI18n from "../../hooks/useI18n";

export interface DropDownProps {
  id: string;
  label: string;
  value: string | null;
  options: Option[];
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  onChange(id: string, value: any | null): void;
}

export interface Option {
  label: string;
  value: string;
}

export const selectOption = { label: "-select-", value: "" };

const DropDown: React.FC<DropDownProps> = ({
  id,
  label,
  value,
  options,
  helperText,
  required,
  disabled,
  multiple,
  onChange,
}) => {
  const { t } = useI18n();

  // Helper to check if a label is a translation key (not dynamic data)
  const isTranslationKey = (label: string): boolean => {
    const translationPrefixes = ['app.', 'auth.', 'errors.', 'fields.', 'form.', 'menu.', 'options.', 'roles.'];
    return translationPrefixes.some(prefix => label.startsWith(prefix));
  };

  // Convert snake_case to Title Case: "global_admin" -> "Global Admin"
  const toTitleCase = (str: string): string => {
    if (!str || !str.includes('_')) return str;
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const translatedOptions = options.map(opt => ({
    ...opt,
    label: isTranslationKey(opt.label) ? t(opt.label) : toTitleCase(opt.label)
  }));

  const translatedSelectOption = {
    ...selectOption,
    label: t(selectOption.label)
  };

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      value={
        multiple
          ? translatedOptions.filter((o) => (value as unknown as any[])?.some(v => String(v) === String(o.value))) || []
          : translatedOptions.find((a) => String(a.value) === String(value)) || translatedSelectOption
      }
      onChange={(_, data: any) => {
        if (multiple) {
          onChange(id, data.map((d: Option) => d.value));
        } else {
          onChange(id, data?.value || "");
        }
      }}
      getOptionLabel={(option) => option.label || ""}
      options={translatedOptions}
      disabled={disabled}
      sx={{ mb: 3, minWidth: { xs: 300, md: 450 } }}
      fullWidth
      disableClearable
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          required={required}
          fullWidth
        />
      )}
    />
  );
};

export default DropDown;
